/**
 * Ted #34 E6.3 (#1161, his Item 27) — tip as a percentage at settle.
 *
 * Ted, 10-02: "when a client adds a tip as a percentage (e.g. 15%), the
 * system should compute it from the requested amount." The desk taps 15 /
 * 18 / 20 % on Collect payment's card leg and the tip field fills with the
 * cents; nobody does the arithmetic.
 *
 * WHAT the percentage is OF: the SERVICE — the visit's amount due without the
 * lines the server adds on top of it (the meal-tracking charge on a boarding
 * stay, a late-pickup daycare charge). A tip on the groom is a tip on the
 * groom, not on a house-food line. The server derives the same base (the
 * booking's own service total) and refuses a charge whose tip is not that
 * percentage, so this file and `payments-admin/service.ts`
 * `tipCentsForPercent` are one formula in two places, kept honest by the
 * refusal rather than by review.
 */

/** The buttons the desk sees, in the order it sees them. */
export const TIP_PERCENT_PRESETS = [15, 18, 20] as const;
export type TipPercentPreset = (typeof TIP_PERCENT_PRESETS)[number];

/**
 * Invoice lines that are NOT the service, and so are never tipped on. The
 * keys are the server's adjustment kinds (`InvoiceAdjustmentRecord.kind`).
 */
export const LINES_NOT_TIPPED: readonly string[] = ['meal_tracking', 'late_pickup'];

/**
 * The service subtotal a percentage applies to: the amount due minus the
 * server's add-on lines. Never negative.
 */
export function tipBaseCents(
  amountDueCents: number,
  adjustments: ReadonlyArray<{ kind: string; deltaCents: number }>,
): number {
  const addOns = adjustments
    .filter((a) => LINES_NOT_TIPPED.includes(a.kind))
    .reduce((sum, a) => sum + a.deltaCents, 0);
  return Math.max(0, amountDueCents - addOns);
}

/** Integer cents, rounded half-up to the cent — the server's rule exactly. */
export function tipCentsForPercent(serviceCents: number, percent: number): number {
  return Math.round((serviceCents * percent) / 100);
}

/** Cents → the "12.34" the tip input holds (no currency sign). */
export function centsToTipInput(cents: number): string {
  return (cents / 100).toFixed(2);
}
